// ========== SUPERADMIN MESSAGING COMPONENT ==========
// Send messages to facilities, view help requests, reply to messages
import React, { useState, useEffect } from 'react';
import { 
  List, 
  Badge, 
  Button, 
  Modal, 
  Input, 
  Select,
  Space,
  Typography,
  Tag,
  Empty,
  notification 
} from 'antd';
import { 
  SendOutlined, 
  InboxOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { database, ref, push, set, onValue, get } from '../../pages/Auth/db/firebase';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

const SuperAdminMessaging = ({ facilities = [] }) => {
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [allMessages, setAllMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  // ========== LISTEN TO ALL FACILITY MESSAGES ==========
  useEffect(() => {
    if (!facilities || facilities.length === 0) return;

    const unsubscribers = [];
    const messagesMap = {};

    facilities.forEach(facility => {
      const messagesRef = ref(database, `messages/${facility.facilityId}`);
      
      const unsubscribe = onValue(messagesRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          messagesMap[facility.facilityId] = Object.entries(data).map(([id, msg]) => ({
            id,
            facilityId: facility.facilityId,
            ...msg
          }));
        } else {
          messagesMap[facility.facilityId] = [];
        }

        // Combine all messages
        const combined = Object.values(messagesMap)
          .flat()
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setAllMessages(combined);

        // Count unread messages to superadmin
        const unread = combined.filter(m => m.status === 'unread' && m.to === 'superadmin').length;
        setUnreadCount(unread);
      });

      unsubscribers.push(unsubscribe);
    });

    // Also listen to global/public messages intended for superadmin (e.g., NEW user requests)
    const globalRef = ref(database, `messages/me`);
    const globalUnsub = onValue(globalRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        messagesMap['me'] = Object.entries(data).map(([id, msg]) => ({ id, facilityId: 'me', ...msg }));
      } else {
        messagesMap['me'] = [];
      }

      const combined = Object.values(messagesMap)
        .flat()
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      setAllMessages(combined);

      const unread = combined.filter(m => m.status === 'unread' && m.to === 'superadmin').length;
      setUnreadCount(unread);
    });
    unsubscribers.push(globalUnsub);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [facilities]);

  // ========== SEND MESSAGE TO FACILITY ==========
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedFacility) {
      api.warning({ message: 'Missing Information', description: 'Please select a facility and enter a message.' });
      return;
    }

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${selectedFacility}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'superadmin',
        to: 'admin',
        message: messageText,
        timestamp: new Date().toISOString(),
        status: 'unread'
      });

      api.success({ message: 'Message Sent', description: `Message sent to ${selectedFacility}.` });
      setMessageText('');
      setSelectedFacility(null);
      setComposeOpen(false);
    } catch (error) {
      console.error('Error sending message:', error);
      api.error({ message: 'Send Failed', description: error.message });
    } finally {
      setSending(false);
    }
  };

  // ========== REPLY TO MESSAGE ==========
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  const handleReply = async () => {
    if (!replyText.trim() || !replyingTo) return;

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${replyingTo.facilityId}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'superadmin',
        to: 'admin',
        message: replyText,
        timestamp: new Date().toISOString(),
        status: 'unread',
        inReplyTo: replyingTo.id
      });

      // Mark original as read
      await markAsRead(replyingTo);

      api.success({ message: 'Reply Sent' });
      setReplyText('');
      setReplyModalOpen(false);
      setReplyingTo(null);
    } catch (error) {
      console.error('Error sending reply:', error);
      api.error({ message: 'Send Failed', description: error.message });
    } finally {
      setSending(false);
    }
  };

  // ========== MARK AS READ ==========
  const markAsRead = async (message) => {
    try {
      const messageRef = ref(database, `messages/${message.facilityId}/${message.id}`);
      const snapshot = await get(messageRef);
      
      if (snapshot.exists()) {
        const msgData = snapshot.val();
        await set(messageRef, {
          ...msgData,
          status: 'read'
        });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  return (
    <>
      {contextHolder}
      
      <Space>
        <Button
          icon={<MessageOutlined />}
          onClick={() => setComposeOpen(true)}
        >
          Send Message
        </Button>
        
        <Badge count={unreadCount} offset={[-5, 5]}>
          <Button
            icon={<InboxOutlined />}
            onClick={() => setMessagesOpen(true)}
          >
            Messages
          </Button>
        </Badge>
      </Space>

      {/* Compose Message Modal */}
      <Modal
        title={<Space><MessageOutlined /> Send Message to Facility</Space>}
        open={composeOpen}
        onCancel={() => {
          setComposeOpen(false);
          setSelectedFacility(null);
          setMessageText('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setComposeOpen(false);
            setSelectedFacility(null);
            setMessageText('');
          }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSendMessage}
          >
            Send Message
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Select Facility:</Text>
            <Select
              style={{ width: '100%', marginTop: '8px' }}
              placeholder="Choose a facility"
              value={selectedFacility}
              onChange={setSelectedFacility}
              size="large"
            >
              {facilities.map(facility => (
                <Option key={facility.facilityId} value={facility.facilityId}>
                  {facility.facilityId}
                </Option>
              ))}
            </Select>
          </div>
          
          <div>
            <Text strong>Message:</Text>
            <TextArea
              rows={6}
              placeholder="Type your message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              maxLength={500}
              showCount
              style={{ marginTop: '8px' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Messages Inbox Modal */}
      <Modal
        title={<Space><InboxOutlined /> All Messages</Space>}
        open={messagesOpen}
        onCancel={() => setMessagesOpen(false)}
        footer={null}
        width={800}
      >
        {allMessages.length === 0 ? (
          <Empty description="No messages" />
        ) : (
          <List
            dataSource={allMessages}
            renderItem={(msg) => (
              <List.Item
                onClick={() => msg.status === 'unread' && msg.to === 'superadmin' && markAsRead(msg)}
                style={{
                  cursor: 'pointer',
                  background: msg.status === 'unread' && msg.to === 'superadmin' ? 'var(--bg-tertiary)' : 'transparent',
                  padding: '12px',
                  borderRadius: '8px'
                }}
              >
                <List.Item.Meta
                  title={
                    <Space size={6} wrap>
                      <Tag color={msg.facilityId === 'me' ? 'geekblue' : 'blue'}>
                        {msg.facilityId === 'me' ? 'Public site' : msg.facilityId}
                      </Tag>
                      <Tag color={msg.from === 'admin' ? 'orange' : msg.from === 'public' ? 'geekblue' : 'green'}>
                        {msg.from === 'admin' ? 'Admin' : msg.from === 'public' ? 'Contact form' : 'You'}
                      </Tag>
                      {msg.subject && (
                        <Tag color="purple">{msg.subject}</Tag>
                      )}
                      {msg.requestLabel && (
                        <Tag color="cyan">
                          {msg.requestLabelFr
                            ? `${msg.requestLabel} / ${msg.requestLabelFr}`
                            : msg.requestLabel}
                        </Tag>
                      )}
                      {msg.status === 'unread' && msg.to === 'superadmin' && (
                        <Badge status="processing" text="New" />
                      )}
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: '8px' }}>
                      {/* Show replied-to message context if this is a reply */}
                      {msg.inReplyTo && (
                        <div style={{ 
                          background: 'var(--bg-tertiary)', 
                          padding: '8px', 
                          borderRadius: '4px',
                          borderLeft: '3px solid var(--primary-color)',
                          marginBottom: '8px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Replying to:
                          </Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              {allMessages.find(m => m.id === msg.inReplyTo)?.message || 'Original message not found'}
                            </Text>
                          </div>
                        </div>
                      )}
                      {(msg.senderName || msg.senderEmail || msg.senderPhone || msg.requestLabel) && (
                        <div
                          style={{
                            background: 'var(--bg-tertiary)',
                            border: '1px solid var(--border-tertiary)',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '12px'
                          }}
                        >
                          <Space direction="vertical" size={2}>
                            {msg.senderName && (
                              <Text strong>{msg.senderName}</Text>
                            )}
                            {msg.senderEmail && (
                              <Text style={{ color: 'var(--text-secondary)' }}>{msg.senderEmail}</Text>
                            )}
                            {msg.senderPhone && (
                              <Text style={{ color: 'var(--text-secondary)' }}>Phone: {msg.senderPhone}</Text>
                            )}
                            {msg.requestLabel && (
                              <Text style={{ color: 'var(--text-secondary)' }}>
                                {msg.requestLabelFr
                                  ? `${msg.requestLabel} / ${msg.requestLabelFr}`
                                  : msg.requestLabel}
                              </Text>
                            )}
                          </Space>
                        </div>
                      )}

                      <Text style={{ color: 'var(--lcolor)' }}>{msg.message}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </Text>
                      </div>
                      {msg.from === 'admin' && (
                        <Button
                          size="small"
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReplyingTo(msg);
                            setReplyModalOpen(true);
                          }}
                        >
                          Reply
                        </Button>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Message"
        open={replyModalOpen}
        onCancel={() => {
          setReplyModalOpen(false);
          setReplyingTo(null);
          setReplyText('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setReplyModalOpen(false);
            setReplyingTo(null);
            setReplyText('');
          }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleReply}
          >
            Send Reply
          </Button>
        ]}
      >
        {replyingTo && (
          <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Replying to {replyingTo.facilityId}:</Text>
            <div><Text >{replyingTo.message}</Text></div>
          </div>
        )}
        <TextArea
          rows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </>
  );
};

export default SuperAdminMessaging;
// ========== SUPERADMIN MESSAGING COMPONENT ENDS ==========
